import { Plus, Trash2 } from "lucide-react";
import {
  useFieldArray,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from "react-hook-form";
import { Button } from "@/components/ui/Button";
import {
  FieldError,
  FieldLabel,
  Input,
  Select,
} from "@/components/ui/Field";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  FIXED_RATE_UNITS,
  QUANTITY_RATE_UNITS,
  RATE_UNIT_LABELS,
  calculateItemAmount,
  decimalToCents,
  decimalToMilli,
  formatCurrency,
  parseDuration,
} from "@/domain/billing";
import type { RateUnitPreset } from "@/domain/types";
import {
  newFormItem,
  resolvedUnitLabel,
  type InvoiceFormValues,
} from "@/features/invoices/invoice-form";

interface Props {
  control: Control<InvoiceFormValues>;
  register: UseFormRegister<InvoiceFormValues>;
  setValue: UseFormSetValue<InvoiceFormValues>;
  errors: FieldErrors<InvoiceFormValues>;
  currency: InvoiceFormValues["currency"];
  defaultBillingType: InvoiceFormValues["items"][number]["billingType"];
  defaultRate: number;
  defaultRateUnit: string;
}

function UnitOptions({
  units,
}: {
  units: RateUnitPreset[];
}) {
  return (
    <>
      {units.map((unit) => (
        <option key={unit} value={unit}>
          {unit === "custom"
            ? "Custom unit"
            : `per ${RATE_UNIT_LABELS[unit]}`}
        </option>
      ))}
    </>
  );
}

export function LineItemEditor({
  control,
  register,
  setValue,
  errors,
  currency,
  defaultBillingType,
  defaultRate,
  defaultRateUnit,
}: Props) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
    keyName: "fieldKey",
  });

  const items =
    useWatch({
      control,
      name: "items",
    }) ?? [];

  const changeBillingType = (
    index: number,
    nextType: InvoiceFormValues["items"][number]["billingType"],
  ) => {
    setValue(`items.${index}.billingType`, nextType, {
      shouldDirty: true,
    });

    if (nextType === "hourly") {
      setValue(`items.${index}.unitPreset`, "hour", {
        shouldDirty: true,
      });

      if ((items[index]?.hourlyRate ?? 0) <= 0) {
        setValue(
          `items.${index}.hourlyRate`,
          defaultBillingType === "hourly"
            ? defaultRate
            : 0,
          { shouldDirty: true },
        );
      }
    }

    if (nextType === "fixed") {
      setValue(`items.${index}.unitPreset`, "project", {
        shouldDirty: true,
      });

      if ((items[index]?.fixedAmount ?? 0) <= 0) {
        setValue(
          `items.${index}.fixedAmount`,
          defaultBillingType === "fixed"
            ? defaultRate
            : 0,
          { shouldDirty: true },
        );
      }
    }

    if (nextType === "quantity") {
      setValue(`items.${index}.unitPreset`, "item", {
        shouldDirty: true,
      });

      if ((items[index]?.unitPrice ?? 0) <= 0) {
        setValue(
          `items.${index}.unitPrice`,
          defaultBillingType === "quantity"
            ? defaultRate
            : 0,
          { shouldDirty: true },
        );
      }
    }
  };

  return (
    <section aria-labelledby="line-items-title">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2
            id="line-items-title"
            className="text-lg font-black text-ink"
          >
            Line items
          </h2>
          <p className="mt-1 text-sm leading-6 text-ink-muted">
            Mix hourly, fixed, and quantity-based
            services. Currency and price unit are saved
            with each invoice.
          </p>
        </div>

        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            append(
              newFormItem(
                defaultBillingType,
                defaultRate,
                defaultRateUnit,
              ),
            )
          }
        >
          <Plus className="h-4 w-4" />
          Add item
        </Button>
      </div>

      <div className="space-y-4">
        {fields.map((field, index) => {
          const item = items[index];
          const type = item?.billingType ?? "hourly";
          const unitPreset =
            item?.unitPreset ??
            (type === "hourly"
              ? "hour"
              : type === "fixed"
                ? "project"
                : "item");

          const unitOptions =
            type === "hourly"
              ? (["hour"] as RateUnitPreset[])
              : type === "fixed"
                ? FIXED_RATE_UNITS
                : QUANTITY_RATE_UNITS;

          const amount = item
            ? calculateItemAmount({
                id: item.id,
                description: item.description,
                billingType: item.billingType,
                durationSeconds:
                  item.billingType === "hourly"
                    ? parseDuration(item.duration) || 0
                    : 0,
                hourlyRateCents:
                  item.billingType === "hourly"
                    ? decimalToCents(item.hourlyRate)
                    : 0,
                quantityMilli:
                  item.billingType === "quantity"
                    ? decimalToMilli(item.quantity)
                    : 0,
                unitLabel: resolvedUnitLabel(item),
                unitPriceCents:
                  item.billingType === "quantity"
                    ? decimalToCents(item.unitPrice)
                    : 0,
                fixedAmountCents:
                  item.billingType === "fixed"
                    ? decimalToCents(item.fixedAmount)
                    : 0,
              })
            : 0;

          return (
            <div
              key={field.fieldKey}
              className="rounded-2xl border border-border bg-surface-muted/50 p-4 sm:p-5"
            >
              <input
                type="hidden"
                {...register(`items.${index}.id`)}
              />

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px_44px] lg:items-start">
                <div>
                  <FieldLabel
                    required
                    htmlFor={`item-${index}-description`}
                  >
                    Description
                  </FieldLabel>
                  <Input
                    id={`item-${index}-description`}
                    placeholder="e.g. Contact Page & Booking Flow"
                    {...register(
                      `items.${index}.description`,
                    )}
                  />
                  <FieldError
                    message={
                      errors.items?.[index]?.description
                        ?.message
                    }
                  />
                </div>

                <div>
                  <FieldLabel
                    htmlFor={`item-${index}-type`}
                  >
                    Calculation
                  </FieldLabel>
                  <Select
                    id={`item-${index}-type`}
                    value={type}
                    onChange={(event) =>
                      changeBillingType(
                        index,
                        event.target
                          .value as InvoiceFormValues["items"][number]["billingType"],
                      )
                    }
                  >
                    <option value="hourly">Hourly</option>
                    <option value="fixed">
                      Fixed amount
                    </option>
                    <option value="quantity">
                      Quantity × rate
                    </option>
                  </Select>
                </div>

                <div className="lg:pt-[26px]">
                  <Tooltip
                    label={`Remove line item ${index + 1}`}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-danger-600 hover:bg-danger-50"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                </div>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {type === "hourly" ? (
                  <>
                    <div>
                      <FieldLabel
                        htmlFor={`item-${index}-duration`}
                      >
                        Duration (HH:MM:SS)
                      </FieldLabel>
                      <Input
                        id={`item-${index}-duration`}
                        placeholder="2:05:34"
                        inputMode="numeric"
                        {...register(
                          `items.${index}.duration`,
                        )}
                      />
                      <FieldError
                        message={
                          errors.items?.[index]?.duration
                            ?.message
                        }
                      />
                    </div>

                    <div>
                      <FieldLabel
                        htmlFor={`item-${index}-hourly-rate`}
                      >
                        Rate ({currency})
                      </FieldLabel>
                      <Input
                        id={`item-${index}-hourly-rate`}
                        type="number"
                        min="0"
                        step="0.01"
                        {...register(
                          `items.${index}.hourlyRate`,
                          { valueAsNumber: true },
                        )}
                      />
                      <FieldError
                        message={
                          errors.items?.[index]
                            ?.hourlyRate?.message
                        }
                      />
                    </div>
                  </>
                ) : null}

                {type === "fixed" ? (
                  <div>
                    <FieldLabel
                      htmlFor={`item-${index}-fixed`}
                    >
                      Fixed amount ({currency})
                    </FieldLabel>
                    <Input
                      id={`item-${index}-fixed`}
                      type="number"
                      min="0"
                      step="0.01"
                      {...register(
                        `items.${index}.fixedAmount`,
                        { valueAsNumber: true },
                      )}
                    />
                    <FieldError
                      message={
                        errors.items?.[index]
                          ?.fixedAmount?.message
                      }
                    />
                  </div>
                ) : null}

                {type === "quantity" ? (
                  <>
                    <div>
                      <FieldLabel
                        htmlFor={`item-${index}-quantity`}
                      >
                        Quantity
                      </FieldLabel>
                      <Input
                        id={`item-${index}-quantity`}
                        type="number"
                        min="0"
                        step="0.001"
                        {...register(
                          `items.${index}.quantity`,
                          { valueAsNumber: true },
                        )}
                      />
                      <FieldError
                        message={
                          errors.items?.[index]?.quantity
                            ?.message
                        }
                      />
                    </div>

                    <div>
                      <FieldLabel
                        htmlFor={`item-${index}-unit-price`}
                      >
                        Price per unit ({currency})
                      </FieldLabel>
                      <Input
                        id={`item-${index}-unit-price`}
                        type="number"
                        min="0"
                        step="0.01"
                        {...register(
                          `items.${index}.unitPrice`,
                          { valueAsNumber: true },
                        )}
                      />
                      <FieldError
                        message={
                          errors.items?.[index]?.unitPrice
                            ?.message
                        }
                      />
                    </div>
                  </>
                ) : null}

                <div>
                  <FieldLabel
                    htmlFor={`item-${index}-unit-preset`}
                  >
                    Price unit
                  </FieldLabel>
                  <Select
                    id={`item-${index}-unit-preset`}
                    {...register(
                      `items.${index}.unitPreset`,
                    )}
                    disabled={type === "hourly"}
                  >
                    <UnitOptions units={unitOptions} />
                  </Select>
                </div>

                {unitPreset === "custom" ? (
                  <div>
                    <FieldLabel
                      required
                      htmlFor={`item-${index}-custom-unit`}
                    >
                      Custom unit
                    </FieldLabel>
                    <Input
                      id={`item-${index}-custom-unit`}
                      placeholder="e.g. CRM setup"
                      {...register(
                        `items.${index}.customUnitLabel`,
                      )}
                    />
                    <FieldError
                      message={
                        errors.items?.[index]
                          ?.customUnitLabel?.message
                      }
                    />
                  </div>
                ) : null}

                <div className="sm:col-start-2 lg:col-start-4">
                  <FieldLabel>Line total</FieldLabel>
                  <div className="flex min-h-11 items-center justify-end rounded-xl border border-border bg-white px-3.5 text-sm font-black text-ink">
                    {formatCurrency(amount, currency)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <FieldError
        message={
          typeof errors.items?.message === "string"
            ? errors.items.message
            : undefined
        }
      />
    </section>
  );
}
